const RECOMMENDATION_SELECTOR = 'kw-cart-recommendations';

function parseIds(value) {
  return (value || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatMoney(cents, currency) {
  const amount = Number(cents);
  if (!Number.isFinite(amount)) return '';

  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(amount / 100);
}

async function fetchRecommendations(productId) {
  const root = window.Shopify?.routes?.root || '/';
  const url = new URL(`${root}recommendations/products.json`, window.location.origin);

  url.searchParams.set('product_id', productId);
  url.searchParams.set('limit', '10');
  url.searchParams.set('intent', 'complementary');

  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) return [];

  const data = await response.json();
  return Array.isArray(data.products) ? data.products : [];
}

class KwCartRecommendations extends HTMLElement {
  static observedAttributes = ['data-source-product-ids', 'data-cart-product-ids', 'data-currency'];

  #requestId = 0;

  connectedCallback() {
    this.load();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) this.load();
  }

  async load() {
    const sourceProductIds = parseIds(this.dataset.sourceProductIds);
    const cartProductIds = parseIds(this.dataset.cartProductIds);
    const currency = this.dataset.currency || 'EUR';
    const cartKey = `${sourceProductIds.join('|')}::${cartProductIds.join('|')}`;
    const inner = this.querySelector('.kw-cart-recommendations__inner');
    const content = this.querySelector('.kw-cart-recommendations__content');

    if (!sourceProductIds.length || !inner || !content) return;
    if (this.dataset.kwLoadedKey === cartKey) return;

    const requestId = ++this.#requestId;
    this.dataset.kwLoading = 'true';

    try {
      let selectedProduct = null;

      for (const sourceProductId of sourceProductIds) {
        const products = await fetchRecommendations(sourceProductId);
        if (requestId !== this.#requestId) return;

        selectedProduct = products.find((product) => !cartProductIds.includes(String(product.id)));
        if (selectedProduct) break;
      }

      if (requestId !== this.#requestId) return;

      if (!selectedProduct) {
        inner.hidden = true;
        content.innerHTML = '';
        this.dataset.kwLoadedKey = cartKey;
        return;
      }

      const image =
        selectedProduct.featured_image ||
        (Array.isArray(selectedProduct.images) && selectedProduct.images.length ? selectedProduct.images[0] : null);
      const price = formatMoney(selectedProduct.price, currency);
      const productUrl = escapeHtml(selectedProduct.url || '#');
      const productTitle = escapeHtml(selectedProduct.title || 'Recommended product');
      const imageMarkup = image
        ? `<img class="kw-cart-recommendations__image" src="${escapeHtml(image)}" alt="${productTitle}" loading="lazy" width="72" height="72">`
        : '';

      content.innerHTML = `
        <a class="kw-cart-recommendations__card" href="${productUrl}">
          ${imageMarkup}
          <div class="kw-cart-recommendations__details">
            <p class="kw-cart-recommendations__title">${productTitle}</p>
            <p class="kw-cart-recommendations__price">${escapeHtml(price)}</p>
            <span class="kw-cart-recommendations__button">View upgrade</span>
          </div>
        </a>
      `;

      inner.hidden = false;
      this.dataset.kwLoadedKey = cartKey;
    } catch (error) {
      if (requestId !== this.#requestId) return;
      inner.hidden = true;
      content.innerHTML = '';
    } finally {
      if (requestId === this.#requestId) delete this.dataset.kwLoading;
    }
  }
}

if (!customElements.get(RECOMMENDATION_SELECTOR)) {
  customElements.define(RECOMMENDATION_SELECTOR, KwCartRecommendations);
}
